import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import { FIELD_LABEL_KEYS } from '../utils/quotationLanguages';
import './Settings.css';

const DEFAULT_QUOTATION_PROMPT = `Je bent een professionele copywriter die zakelijke, wervende teksten schrijft voor offertes.
Je schrijft helder, overtuigend en professioneel.
Gebruik een actieve schrijfstijl en focus op de voordelen voor de klant.
Houd de teksten beknopt maar impactvol.`;

const Settings = ({ onClose, asPage }) => {
  const navigate = useNavigate();
  const { user, selectedSubscriberId, loadSubscribers: reloadGlobalSubscribers, isLicenseActive, isTrialExpired, trialDaysLeft } = useAuth();
  const isPage = asPage === true;

  const [subscribers, setSubscribers] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [licenseRequestSent, setLicenseRequestSent] = useState(false);
  const [licenseRequestLoading, setLicenseRequestLoading] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(90);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [bijlageStats, setBijlageStats] = useState(null);
  const [bijlageStatsLoading, setBijlageStatsLoading] = useState(false);
  const [fixCompanyLoading, setFixCompanyLoading] = useState(false);
  const [fixCompanyResult, setFixCompanyResult] = useState(null);
  const [werkUrl, setWerkUrl] = useState(user?.werk_url || '');
  const [werkUrlSaving, setWerkUrlSaving] = useState(false);

  // Onboarding: Odoo-eerst flow
  const [onboardOdoo, setOnboardOdoo] = useState({ url: '', database: '', username: '', password: '' });
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [companySettings, setCompanySettings] = useState({
    offertegpt_api_url: '',
    openai_api_key: '',
    openai_quotation_prompt: DEFAULT_QUOTATION_PROMPT,
    logo_url: '',
    quotation_field_labels: {},
    quotation_field_labels_en: {},
    company_name: '',
    company_details: { address: '', phone: '', email: '', kvk: '', btw: '', bank: '', persoon: '', payoff: '', offer_request_api_key: '', api_customer_type: 'bedrijf', standaardinhoud_templates: [] },
    odoo_url: '',
    odoo_database: '',
    odoo_username: '',
    odoo_password: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadSubscribers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.subscriberId, selectedSubscriberId]);

  useEffect(() => {
    if (selectedCompanyId) loadCompanySettings(selectedCompanyId);
    else setCompanySettings({
      offertegpt_api_url: '', offertegpt_api_key: '', openai_api_key: '', openai_quotation_prompt: DEFAULT_QUOTATION_PROMPT,
      logo_url: '', quotation_field_labels: {}, quotation_field_labels_en: {},
      company_name: '', company_details: { address: '', phone: '', email: '', kvk: '', btw: '', bank: '', persoon: '', payoff: '', offer_request_api_key: '', api_customer_type: 'bedrijf', standaardinhoud_templates: [] },
      odoo_url: '', odoo_database: '', odoo_username: '', odoo_password: ''
    });
  }, [selectedCompanyId]);

  const loadSubscribers = async () => {
    try {
      // Backend retourneert alleen bedrijven van de ingelogde user (voor alle rollen)
      const res = await api.get('/api/subscribers');
      const list = res.data || [];
      setSubscribers(list);
      if (list.length > 0) setSelectedCompanyId(prev => prev || list[0].id);
      else setSelectedCompanyId('');
    } catch (err) {
      console.error('Subscribers laden mislukt:', err);
      setSubscribers([]);
      setSelectedCompanyId('');
    }
  };

  const handleSyncFromOdoo = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const res = await api.post('/api/subscribers/sync-from-odoo', {
        odoo_url: onboardOdoo.url,
        odoo_database: onboardOdoo.database,
        odoo_username: onboardOdoo.username,
        odoo_password: onboardOdoo.password
      });
      setSyncResult(res.data);
      // Herlaad de bedrijvenlijst in Settings EN in de header dropdown
      await loadSubscribers();
      await reloadGlobalSubscribers();
      setSuccess(`Sync voltooid: ${res.data.message}. Kies hieronder je bedrijf en vul de overige instellingen in.`);
    } catch (err) {
      setSyncError(err.response?.data?.message || err.response?.data?.error || 'Sync mislukt');
    } finally {
      setSyncing(false);
    }
  };

  const loadCompanySettings = async (subscriberId) => {
    try {
      const res = await api.get(`/api/subscribers/${subscriberId}`);
      const s = res.data;
      const cd = s.company_details || {};
      setCompanySettings({
        offertegpt_api_url: s.offertegpt_api_url || '',
        offertegpt_api_key: s.offertegpt_api_key || '',
        openai_api_key: s.openai_api_key || '',
        openai_quotation_prompt: s.openai_quotation_prompt || DEFAULT_QUOTATION_PROMPT,
        logo_url: s.logo_url || '',
        quotation_field_labels: s.quotation_field_labels || {},
        quotation_field_labels_en: s.quotation_field_labels_en || {},
        company_name: s.company_name || '',
        company_details: {
          ...cd,  // Bewaar alle bestaande velden (odoo_company_id, vat, etc.)
          address: cd.address || '',
          phone: cd.phone || '',
          email: cd.email || '',
          kvk: cd.kvk || '',
          btw: cd.btw || '',
          bank: cd.bank || '',
          persoon: cd.persoon || '',
          payoff: cd.payoff || '',
          offer_request_api_key: cd.offer_request_api_key || '',
          api_customer_type: cd.api_customer_type || 'bedrijf',
          standaardinhoud_templates: Array.isArray(cd.standaardinhoud_templates) ? cd.standaardinhoud_templates :
            (cd.standaardinhoud ? [{ name: 'Standaard', content: cd.standaardinhoud }] : [])
        },
        odoo_url: s.odoo_url || '',
        odoo_database: s.odoo_database || '',
        odoo_username: s.odoo_username || '',
        odoo_password: s.odoo_password === '***' ? '' : (s.odoo_password || '')
      });
    } catch (err) {
      console.error('Bedrijfsinstellingen laden mislukt:', err);
    }
  };

  const handleCompanyChange = (field, value) => {
    setCompanySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleCompanyDetailsChange = (field, value) => {
    setCompanySettings(prev => ({
      ...prev,
      company_details: { ...(prev.company_details || {}), [field]: value }
    }));
  };

  const handleSaveWerkUrl = async () => {
    setWerkUrlSaving(true);
    try {
      await api.put('/api/auth/profile', { werk_url: werkUrl || null });
      setSuccess('Werk-URL opgeslagen.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Werk-URL opslaan mislukt');
    } finally {
      setWerkUrlSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Globale instellingen alleen voor admins
      if (user?.role === 'admin') {
        const full = await api.get('/api/settings').then(r => r.data);
        const { odoo, ...rest } = full;
        await api.put('/api/settings', rest);
      }
      if (selectedCompanyId) {
        const payload = {
          logo_url: companySettings.logo_url,
          company_name: companySettings.company_name,
          company_details: companySettings.company_details,
          openai_api_key: companySettings.openai_api_key,
          openai_quotation_prompt: companySettings.openai_quotation_prompt,
          offertegpt_api_url: companySettings.offertegpt_api_url,
          offertegpt_api_key: companySettings.offertegpt_api_key,
          quotation_field_labels: companySettings.quotation_field_labels,
          quotation_field_labels_en: companySettings.quotation_field_labels_en
        };
        if (companySettings.odoo_password) {
          payload.odoo_password = companySettings.odoo_password;
        }
        if (user?.role === 'admin') {
          payload.odoo_url = companySettings.odoo_url || undefined;
          payload.odoo_database = companySettings.odoo_database || undefined;
          payload.odoo_username = companySettings.odoo_username || undefined;
          payload.odoo_password = companySettings.odoo_password || undefined;
        }
        await api.put(`/api/subscribers/${selectedCompanyId}`, payload);
      }
      setSuccess('Instellingen succesvol opgeslagen!');
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (isPage) navigate('/');
    else if (onClose) onClose();
  };

  const content = (
    <>
      <div className={isPage ? 'settings-page-header' : 'settings-header'}>
        <h2>Instellingen</h2>
        {isPage ? (
          <button type="button" className="btn btn-secondary btn-small" onClick={handleBack}>
            ← Terug naar overzicht
          </button>
        ) : (
          <button type="button" className="close-btn" onClick={onClose} aria-label="Sluiten">×</button>
        )}
      </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <form onSubmit={handleSave}>
          <div className="settings-block">
            <h2 className="settings-block-title">Bedrijven</h2>
            {subscribers.length === 0 && (
              <div className="settings-section" style={{ marginBottom: 24 }}>
                <h3>Stap 1: Koppel je Odoo-omgeving</h3>
                <p className="settings-help" style={{ marginBottom: 12 }}>
                  Vul hieronder je eigen Odoo-gegevens in en klik op &quot;Sync bedrijven&quot;. Je bedrijven worden automatisch opgehaald en verschijnen in de dropdown.
                </p>
                <div className="input-group">
                  <label>Odoo URL</label>
                  <input
                    type="text"
                    value={onboardOdoo.url}
                    onChange={(e) => setOnboardOdoo(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://your-odoo-instance.com"
                    disabled={syncing}
                  />
                </div>
                <div className="input-group">
                  <label>Database</label>
                  <input
                    type="text"
                    value={onboardOdoo.database}
                    onChange={(e) => setOnboardOdoo(prev => ({ ...prev, database: e.target.value }))}
                    placeholder="Database naam"
                    disabled={syncing}
                  />
                </div>
                <div className="input-group">
                  <label>Gebruikersnaam</label>
                  <input
                    type="text"
                    value={onboardOdoo.username}
                    onChange={(e) => setOnboardOdoo(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Odoo gebruikersnaam"
                    disabled={syncing}
                  />
                </div>
                <div className="input-group">
                  <label>Wachtwoord</label>
                  <input
                    type="password"
                    value={onboardOdoo.password}
                    onChange={(e) => setOnboardOdoo(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Odoo wachtwoord"
                    disabled={syncing}
                  />
                </div>
                {syncError && <div className="error" style={{ marginBottom: 8 }}>{syncError}</div>}
                {syncResult && <div className="success" style={{ marginBottom: 8 }}>{syncResult.message}</div>}
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={syncing || !onboardOdoo.url || !onboardOdoo.username}
                  onClick={handleSyncFromOdoo}
                >
                  {syncing ? 'Bezig met synchroniseren…' : 'Sync bedrijven uit Odoo'}
                </button>
              </div>
            )}
            <div className="settings-section">
              <div className="input-group">
                <label>Bedrijf</label>
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                >
                  <option value="">— Kies een bedrijf —</option>
                  {subscribers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.company_name || s.companyName || 'Onbenoemd'}
                    </option>
                  ))}
                </select>
                <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Onderstaande instellingen gelden voor het gekozen bedrijf en worden in de workflow gebruikt.
                </small>
              </div>
            </div>
            {selectedCompanyId && (
              <>
                <div className="settings-section" style={{ marginBottom: 24 }}>
                  <h3>Mijn werkdomein</h3>
                  <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Het domein waarmee je Odoo opent in de browser (bijv. kantoor-support.nl). Dit wordt gebruikt voor productlinks in de offertebouwer.
                  </p>
                  <div className="input-group">
                    <label>Werk-URL</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="url"
                        value={werkUrl}
                        onChange={(e) => setWerkUrl(e.target.value)}
                        placeholder="https://www.kantoor-support.nl"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-small"
                        onClick={handleSaveWerkUrl}
                        disabled={werkUrlSaving}
                      >
                        {werkUrlSaving ? '...' : 'Opslaan'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="settings-section" style={{ marginBottom: 24 }}>
                  <h3>Odoo CRM (dit bedrijf)</h3>
                  <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    {user?.role === 'admin'
                      ? 'Vul hier de Odoo-gegevens in voor dit bedrijf.'
                      : 'Odoo-verbinding wordt beheerd door de beheerder.'}
                  </p>
                  <div className="input-group">
                    <label>Odoo URL</label>
                    <input
                      type="text"
                      value={companySettings.odoo_url || ''}
                      onChange={(e) => handleCompanyChange('odoo_url', e.target.value)}
                      placeholder="https://your-odoo-instance.com"
                      disabled={user?.role !== 'admin'}
                      style={user?.role !== 'admin' ? { color: '#9ca3af', background: '#f3f4f6' } : {}}
                    />
                  </div>
                  <div className="input-group">
                    <label>Database</label>
                    <input
                      type="text"
                      value={companySettings.odoo_database || ''}
                      onChange={(e) => handleCompanyChange('odoo_database', e.target.value)}
                      placeholder="Database naam"
                      disabled={user?.role !== 'admin'}
                      style={user?.role !== 'admin' ? { color: '#9ca3af', background: '#f3f4f6' } : {}}
                    />
                  </div>
                  <div className="input-group">
                    <label>Gebruikersnaam</label>
                    <input
                      type="text"
                      value={companySettings.odoo_username || ''}
                      onChange={(e) => handleCompanyChange('odoo_username', e.target.value)}
                      placeholder="Odoo gebruikersnaam"
                      disabled={user?.role !== 'admin'}
                      style={user?.role !== 'admin' ? { color: '#9ca3af', background: '#f3f4f6' } : {}}
                    />
                  </div>
                  <div className="input-group">
                    <label>Wachtwoord</label>
                    <input
                      type="password"
                      value={companySettings.odoo_password || ''}
                      onChange={(e) => handleCompanyChange('odoo_password', e.target.value)}
                      placeholder="Odoo wachtwoord"
                    />
                  </div>
                </div>
                <div className="settings-section">
                  <h3>Bedrijfsgegevens (footer offerte-PDF)</h3>
                  <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Wordt getoond in de footer van de offerte-PDF. Vul in wat van toepassing is.
                  </p>
                  <div className="input-group">
                    <label>Bedrijfsnaam</label>
                    <input
                      type="text"
                      value={companySettings.company_name || ''}
                      onChange={(e) => handleCompanyChange('company_name', e.target.value)}
                      placeholder="Naam bedrijf"
                    />
                  </div>
                  <div className="input-group">
                    <label>Adres</label>
                    <input
                      type="text"
                      value={companySettings.company_details?.address || ''}
                      onChange={(e) => handleCompanyDetailsChange('address', e.target.value)}
                      placeholder="Straat, postcode en plaats"
                    />
                  </div>
                  <div className="input-group">
                    <label>Telefoon</label>
                    <input
                      type="text"
                      value={companySettings.company_details?.phone || ''}
                      onChange={(e) => handleCompanyDetailsChange('phone', e.target.value)}
                      placeholder="Telefoonnummer"
                    />
                  </div>
                  <div className="input-group">
                    <label>E-mail</label>
                    <input
                      type="email"
                      value={companySettings.company_details?.email || ''}
                      onChange={(e) => handleCompanyDetailsChange('email', e.target.value)}
                      placeholder="E-mailadres"
                    />
                  </div>
                  <div className="input-group">
                    <label>KvK</label>
                    <input
                      type="text"
                      value={companySettings.company_details?.kvk || ''}
                      onChange={(e) => handleCompanyDetailsChange('kvk', e.target.value)}
                      placeholder="KvK-nummer"
                    />
                  </div>
                  <div className="input-group">
                    <label>BTW-nummer</label>
                    <input
                      type="text"
                      value={companySettings.company_details?.btw || ''}
                      onChange={(e) => handleCompanyDetailsChange('btw', e.target.value)}
                      placeholder="BTW-id"
                    />
                  </div>
                  <div className="input-group">
                    <label>Bank (IBAN e.d.)</label>
                    <input
                      type="text"
                      value={companySettings.company_details?.bank || ''}
                      onChange={(e) => handleCompanyDetailsChange('bank', e.target.value)}
                      placeholder="IBAN of bankgegevens"
                    />
                  </div>
                  <div className="input-group">
                    <label>Ondertekenaar (persoon)</label>
                    <input
                      type="text"
                      value={companySettings.company_details?.persoon || ''}
                      onChange={(e) => handleCompanyDetailsChange('persoon', e.target.value)}
                      placeholder="Bijv. Jan Jansen, Directeur"
                    />
                    <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Naam van de ondertekenaar; staat in de offerte onder &quot;Met vriendelijke groet&quot;, daarna komt de bedrijfsnaam.
                    </small>
                  </div>
                  <div className="input-group">
                    <label>Steunkleur (huisstijl)</label>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <input
                        type="color"
                        value={companySettings.company_details?.brand_color || '#290B50'}
                        onChange={(e) => handleCompanyDetailsChange('brand_color', e.target.value)}
                        style={{ width: 48, height: 40, padding: 2, border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={companySettings.company_details?.brand_color || '#290B50'}
                        onChange={(e) => {
                          let v = e.target.value.trim();
                          if (v && !v.startsWith('#')) v = '#' + v;
                          handleCompanyDetailsChange('brand_color', v);
                        }}
                        placeholder="#290B50"
                        style={{ width: 120, fontFamily: 'monospace', fontSize: 14 }}
                      />
                      <div
                        style={{
                          width: 80, height: 40, borderRadius: 8,
                          background: companySettings.company_details?.brand_color || '#290B50',
                          border: '1px solid #d1d5db',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 11, fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,.3)'
                        }}
                      >
                        Preview
                      </div>
                    </div>
                    <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Wordt gebruikt als accentkleur in koppen en highlights van de offerte-PDF.
                    </small>
                  </div>
                  <div className="api-key-section">
                    <h4 style={{ margin: '0 0 8px', color: '#290B50', fontSize: '14px' }}>API voor offerte-aanvragen</h4>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 12px' }}>
                      Externe systemen (website, CRM) kunnen hiermee offerte-aanvragen insturen. De aanvragen verschijnen in het <strong>Aanvragendashboard</strong>.
                    </p>

                    <div className="api-key-display">
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Formulier-URL (POST)</label>
                      <small style={{ color: '#6b7280', display: 'block', marginBottom: 6, fontSize: '11px' }}>
                        Gebruik deze volledige URL als action-URL in je formulier. API-sleutel zit in de URL.
                      </small>
                      <div className="api-key-row">
                        <code className="api-key-value" style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                          {window.location.origin}/api/offer-requests/{selectedCompanyId}?api_key={companySettings.company_details?.offer_request_api_key || '...'}
                        </code>
                        <button
                          type="button"
                          className="btn-copy"
                          onClick={() => {
                            const fullUrl = `${window.location.origin}/api/offer-requests/${selectedCompanyId}?api_key=${companySettings.company_details?.offer_request_api_key || ''}`;
                            navigator.clipboard.writeText(fullUrl);
                            setApiKeyCopied('endpoint');
                            setTimeout(() => setApiKeyCopied(false), 2000);
                          }}
                          title="Kopieer volledige URL"
                          disabled={!companySettings.company_details?.offer_request_api_key}
                        >
                          {apiKeyCopied === 'endpoint' ? '✓ Gekopieerd' : 'Kopieer'}
                        </button>
                        <button
                          type="button"
                          className="btn-regenerate"
                          onClick={async () => {
                            if (!window.confirm('Weet je zeker dat je een nieuwe API-sleutel wilt genereren? De oude sleutel werkt dan niet meer.')) return;
                            setRegeneratingKey(true);
                            try {
                              const res = await api.post(`/api/subscribers/${selectedCompanyId}/regenerate-api-key`);
                              if (res.data?.api_key) {
                                handleCompanyDetailsChange('offer_request_api_key', res.data.api_key);
                              }
                            } catch (err) {
                              alert('Regenereren mislukt: ' + (err.response?.data?.error || err.message));
                            } finally {
                              setRegeneratingKey(false);
                            }
                          }}
                          disabled={regeneratingKey}
                          title="Genereer nieuwe sleutel"
                        >
                          {regeneratingKey ? '...' : 'Hernieuwen'}
                        </button>
                      </div>
                    </div>

                    <div className="input-group" style={{ marginTop: 16 }}>
                      <label>Klanttype bij API-aanvragen</label>
                      <select
                        value={companySettings.company_details?.api_customer_type || 'bedrijf'}
                        onChange={(e) => handleCompanyDetailsChange('api_customer_type', e.target.value)}
                      >
                        <option value="bedrijf">Overwegend bedrijven (B2B)</option>
                        <option value="particulier">Overwegend particulieren (B2C)</option>
                      </select>
                      <small style={{ color: '#6b7280', marginTop: 4, display: 'block' }}>
                        {companySettings.company_details?.api_customer_type === 'particulier'
                          ? 'Contactgegevens uit de API worden als particuliere klant ingevuld.'
                          : 'Contactgegevens uit de API worden als bedrijf + contactpersoon ingevuld.'}
                      </small>
                    </div>

                    <details style={{ marginTop: 16 }}>
                      <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#6A1B9A', fontWeight: 600 }}>Voorbeeld API-aanroep (cURL)</summary>
                      <pre className="api-example-code">{`curl -X POST \\
  "${window.location.origin}/api/offer-requests/${selectedCompanyId}?api_key=${companySettings.company_details?.offer_request_api_key || '<jouw-api-sleutel>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
  "aanvrager": "Website formulier",
  "opmerkingen": "Klant wil een offerte ontvangen.",
  "products": [
    { "product_id": "uuid-van-product" },
    { "name": "Extra werk", "price": 500, "quantity": 1 }
  ],
  "customer": {
    "contact_name": "Jan de Vries",
    "contact_email": "jan@bedrijf.nl",
    "contact_phone": "06 12345678"
  }
}'`}</pre>
                    </details>
                  </div>
                </div>

                <div className="settings-section">
                  <h3>Standaardinhoud-templates</h3>
                  <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Maak templates die je bij het aanmaken van een offerte als dropdown kunt kiezen.
                    Staat tussen <strong>Investering</strong> en <strong>Afsluiting</strong> in de offerte-PDF.
                  </p>
                  {(companySettings.company_details?.standaardinhoud_templates || []).map((tpl, idx) => (
                    <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 12, background: '#fafafa' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <input
                          type="text"
                          value={tpl.name || ''}
                          onChange={(e) => {
                            const updated = [...(companySettings.company_details?.standaardinhoud_templates || [])];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            handleCompanyDetailsChange('standaardinhoud_templates', updated);
                          }}
                          placeholder="Naam template, bijv. Website, SEO-voorstel..."
                          style={{ flex: 1, fontWeight: 600 }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          style={{ color: '#ef4444', borderColor: '#fca5a5', whiteSpace: 'nowrap' }}
                          onClick={() => {
                            const updated = (companySettings.company_details?.standaardinhoud_templates || []).filter((_, i) => i !== idx);
                            handleCompanyDetailsChange('standaardinhoud_templates', updated);
                          }}
                        >
                          Verwijderen
                        </button>
                      </div>
                      <textarea
                        value={tpl.content || ''}
                        onChange={(e) => {
                          const updated = [...(companySettings.company_details?.standaardinhoud_templates || [])];
                          updated[idx] = { ...updated[idx], content: e.target.value };
                          handleCompanyDetailsChange('standaardinhoud_templates', updated);
                        }}
                        placeholder="HTML-inhoud voor deze template..."
                        rows={6}
                        style={{ fontFamily: 'inherit', fontSize: '13px', width: '100%' }}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={() => {
                      const updated = [...(companySettings.company_details?.standaardinhoud_templates || []), { name: '', content: '' }];
                      handleCompanyDetailsChange('standaardinhoud_templates', updated);
                    }}
                  >
                    + Template toevoegen
                  </button>
                </div>

                <div className="settings-section">
                  <h3>AI Instellingen (OpenAI)</h3>
                  <small style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px', display: 'block' }}>
                    OpenAI wordt gebruikt voor het genereren van offerteteksten. Verkrijg een API key op <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com/api-keys</a>.
                  </small>
                  <div className="input-group">
                    <label>API Key (OpenAI)</label>
                    <input
                      type="password"
                      value={companySettings.openai_api_key || ''}
                      onChange={(e) => handleCompanyChange('openai_api_key', e.target.value)}
                      placeholder="sk-..."
                    />
                  </div>
                </div>

                <div className="settings-section">
                  <h3>Offertekennis (AI prompt)</h3>
                  <small style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px', display: 'block' }}>
                    Beschrijf hier de kennis en schrijfstijl voor dit bedrijf. Dit wordt als instructie meegegeven aan de AI bij het genereren van offerteteksten.
                  </small>
                  <div className="input-group">
                    <label>Instructies voor de AI</label>
                    <textarea
                      value={companySettings.openai_quotation_prompt || ''}
                      onChange={(e) => handleCompanyChange('openai_quotation_prompt', e.target.value)}
                      placeholder="Je bent een professionele copywriter die offertes schrijft voor [bedrijfsnaam]. Je kent onze producten, diensten en schrijfstijl..."
                      rows={8}
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                    <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Tip: kopieer hier de kennis/instructies uit je Custom GPT. Dit verschilt per bedrijf.
                    </small>
                  </div>
                </div>

                <div className="settings-section">
                  <h3>Logo voor offerte</h3>
                  <div className="input-group">
                    <label>Afbeelding</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => handleCompanyChange('logo_url', reader.result);
                        reader.readAsDataURL(file);
                      }}
                    />
                    {companySettings.logo_url && (
                      <div style={{ marginTop: 8 }}>
                        <img src={companySettings.logo_url} alt="Logo preview" style={{ maxHeight: 60, maxWidth: 180 }} />
                        <button type="button" className="btn btn-secondary btn-small" style={{ marginTop: 4 }} onClick={() => handleCompanyChange('logo_url', '')}>
                          Logo verwijderen
                        </button>
                      </div>
                    )}
                    <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Wordt getoond op de gegenereerde offerte-PDF (bijv. PNG of JPG, max. 500 KB aanbevolen).
                    </small>
                  </div>
                  <div className="input-group" style={{ marginTop: 12 }}>
                    <label>Payoff / tagline</label>
                    <input
                      type="text"
                      value={companySettings.company_details?.payoff || ''}
                      onChange={(e) => handleCompanyDetailsChange('payoff', e.target.value)}
                      placeholder="bijv. Slimmer groeien met AI & creativiteit"
                    />
                    <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Wordt onder het logo getoond op de offerte-PDF.
                    </small>
                  </div>
                </div>

                <div className="settings-section">
                  <h3>Standaard betalingsvoorwaarden</h3>
                  <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Stel de standaard betalingspercentages in voor nieuwe offertes van dit bedrijf.
                  </p>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="input-group" style={{ flex: '1', minWidth: '140px' }}>
                      <label>% vooruitbetaling</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={companySettings.company_details?.default_payment_prepayment ?? ''}
                        onChange={(e) => handleCompanyDetailsChange('default_payment_prepayment', e.target.value ? parseInt(e.target.value, 10) : '')}
                        placeholder="bijv. 25"
                      />
                    </div>
                    <div className="input-group" style={{ flex: '1', minWidth: '140px' }}>
                      <label>% bij oplevering</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={companySettings.company_details?.default_payment_delivery ?? ''}
                        onChange={(e) => handleCompanyDetailsChange('default_payment_delivery', e.target.value ? parseInt(e.target.value, 10) : '')}
                        placeholder="bijv. 75"
                      />
                    </div>
                  </div>
                  {companySettings.company_details?.default_payment_prepayment != null && companySettings.company_details?.default_payment_delivery != null && (
                    <p style={{ fontSize: '12px', color: '#374151', marginTop: '8px' }}>
                      Voorbeeld: <strong>{companySettings.company_details.default_payment_prepayment}% / {companySettings.company_details.default_payment_delivery}%</strong> — {companySettings.company_details.default_payment_prepayment}% aanbetaling bij akkoord • {companySettings.company_details.default_payment_delivery}% bij oplevering
                    </p>
                  )}
                </div>

                <div className="settings-section">
                  <h3>Offerte veldnamen (Nederlands)</h3>
                  <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Labels op de Nederlandse offerte-PDF en in het voorbeeld.
                  </p>
                  {FIELD_LABEL_KEYS.map(({ key, labelNl, placeholderNl }) => (
                    <div key={key} className="input-group">
                      <label>{labelNl}</label>
                      <input
                        type="text"
                        value={companySettings.quotation_field_labels?.[key] ?? ''}
                        onChange={(e) => handleCompanyChange('quotation_field_labels', {
                          ...(companySettings.quotation_field_labels || {}),
                          [key]: e.target.value || undefined
                        })}
                        placeholder={placeholderNl}
                      />
                    </div>
                  ))}
                </div>

                <div className="settings-section">
                  <h3>Offerte veldnamen (English)</h3>
                  <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Labels op de Engelse offerte-PDF wanneer 🇬🇧 English is gekozen bij het maken van een offerte.
                  </p>
                  {FIELD_LABEL_KEYS.map(({ key, labelNl, placeholderEn }) => (
                    <div key={key} className="input-group">
                      <label>{labelNl}</label>
                      <input
                        type="text"
                        value={companySettings.quotation_field_labels_en?.[key] ?? ''}
                        onChange={(e) => handleCompanyChange('quotation_field_labels_en', {
                          ...(companySettings.quotation_field_labels_en || {}),
                          [key]: e.target.value || undefined
                        })}
                        placeholder={placeholderEn}
                      />
                    </div>
                  ))}
                </div>

              </>
            )}
          </div>

          {/* Licentie sectie – altijd zichtbaar, onafhankelijk van bedrijfsselectie */}
          {user?.role !== 'admin' && (
            <div className="settings-section" style={{ marginTop: 24 }}>
              <h3>Licentie</h3>
              {isLicenseActive ? (
                <p style={{ color: '#16a34a', fontWeight: 600, fontSize: '14px' }}>
                  Uw licentie is actief.
                </p>
              ) : (
                <>
                  {isTrialExpired ? (
                    <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: 12 }}>
                      Uw proefperiode is verlopen. Dien een licentieverzoek in of voer uw licentiecode in via de banner bovenaan.
                    </p>
                  ) : trialDaysLeft > 0 ? (
                    <p style={{ color: '#92400e', fontSize: '14px', marginBottom: 12 }}>
                      U heeft nog <strong>{trialDaysLeft}</strong> {trialDaysLeft === 1 ? 'dag' : 'dagen'} proefperiode over.
                    </p>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: 12 }}>
                      U heeft geen actieve licentie. Dien een verzoek in om een licentiecode te ontvangen.
                    </p>
                  )}

                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 16, fontSize: '13px', color: '#374151', lineHeight: '1.7' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: '14px', color: '#1B3A6B' }}>Wat houdt een licentieverzoek in?</p>
                    <p style={{ margin: 0 }}>
                      Na uw verzoek wordt een jaarabonnement aangemaakt. De kosten bedragen
                      <strong> &euro; 275,&ndash; per jaar</strong> (excl. btw). U ontvangt een licentiecode waarmee u
                      OfferteRunner onbeperkt kunt gebruiken.
                    </p>
                    <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Offertetemplates kunt u zelf aanmaken. Wilt u dat wij dit voor u doen?
                      Dat kan voor <strong>&euro;&nbsp;75,&ndash;</strong> per deeltemplate (per onderwerp).
                    </p>
                  </div>

                  {licenseRequestSent ? (
                    <p style={{ color: '#16a34a', fontSize: '14px' }}>
                      Licentieverzoek ingediend! De beheerder neemt contact met u op.
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={licenseRequestLoading}
                      onClick={async () => {
                        setLicenseRequestLoading(true);
                        try {
                          await api.post('/api/license/request');
                          setLicenseRequestSent(true);
                        } catch (err) {
                          alert(err.response?.data?.error || 'Verzoek indienen mislukt.');
                        } finally {
                          setLicenseRequestLoading(false);
                        }
                      }}
                    >
                      {licenseRequestLoading ? 'Bezig...' : 'Licentieverzoek indienen'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {selectedCompanyId && (
            <div className="settings-section" style={{ borderTop: '2px solid #fca5a5', paddingTop: 16, marginTop: 24 }}>
              <h3 style={{ color: '#dc2626' }}>Offertebijlages opschonen</h3>

              {/* Stats */}
              <div style={{ marginBottom: 16 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  disabled={bijlageStatsLoading}
                  onClick={async () => {
                    setBijlageStatsLoading(true);
                    setBijlageStats(null);
                    try {
                      const res = await api.get(`/api/subscribers/${selectedCompanyId}/offertebijlage-stats`);
                      setBijlageStats(res.data);
                    } catch (err) {
                      setBijlageStats({ error: err.response?.data?.error || 'Ophalen mislukt' });
                    } finally {
                      setBijlageStatsLoading(false);
                    }
                  }}
                >
                  {bijlageStatsLoading ? 'Laden...' : 'Statistieken ophalen'}
                </button>
                {bijlageStats && !bijlageStats.error && (
                  <div style={{ marginTop: 10, fontSize: 13, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                      <span style={{ color: '#6b7280' }}>Totaal actief:</span>
                      <strong>{bijlageStats.active}</strong>
                      <span style={{ color: '#6b7280' }}>Met bedrijf:</span>
                      <span>{bijlageStats.withCompany}</span>
                      <span style={{ color: '#6b7280' }}>Zonder bedrijf:</span>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>{bijlageStats.withoutCompany}</span>
                    </div>
                    {bijlageStats.withoutCompany > 0 && (
                      <div style={{ marginTop: 8, marginBottom: 8 }}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          style={{ fontSize: 12 }}
                          disabled={fixCompanyLoading}
                          onClick={async () => {
                            setFixCompanyLoading(true);
                            setFixCompanyResult(null);
                            try {
                              const res = await api.post(`/api/subscribers/${selectedCompanyId}/fix-offertebijlage-company`);
                              setFixCompanyResult({ success: true, message: res.data?.message || 'Gekoppeld.' });
                              // Refresh stats
                              const statsRes = await api.get(`/api/subscribers/${selectedCompanyId}/offertebijlage-stats`);
                              setBijlageStats(statsRes.data);
                            } catch (err) {
                              setFixCompanyResult({ success: false, message: err.response?.data?.error || 'Koppelen mislukt.' });
                            } finally {
                              setFixCompanyLoading(false);
                            }
                          }}
                        >
                          {fixCompanyLoading ? 'Bezig...' : `Koppel ${bijlageStats.withoutCompany} bijlage(s) aan dit bedrijf`}
                        </button>
                        {fixCompanyResult && (
                          <span style={{ marginLeft: 8, fontSize: 12, color: fixCompanyResult.success ? '#065f46' : '#991b1b' }}>
                            {fixCompanyResult.message}
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                      {bijlageStats.oldest && (
                        <>
                          <span style={{ color: '#6b7280' }}>Oudste:</span>
                          <span>{bijlageStats.oldest.daysAgo} dagen geleden ({new Date(bijlageStats.oldest.date).toLocaleDateString('nl-NL')})</span>
                        </>
                      )}
                      {bijlageStats.newest && (
                        <>
                          <span style={{ color: '#6b7280' }}>Nieuwste:</span>
                          <span>{bijlageStats.newest.daysAgo} dagen geleden ({new Date(bijlageStats.newest.date).toLocaleDateString('nl-NL')})</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {bijlageStats?.error && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#991b1b' }}>{bijlageStats.error}</div>
                )}
              </div>

              {/* Automatisch archiveren toggle */}
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <label className="toggle-switch" style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={!!companySettings.company_details?.offertebijlage_auto_archive}
                      onChange={(e) => handleCompanyDetailsChange('offertebijlage_auto_archive', e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 24,
                      background: companySettings.company_details?.offertebijlage_auto_archive ? '#f97316' : '#d1d5db',
                      transition: 'background 0.2s'
                    }}>
                      <span style={{
                        position: 'absolute', left: companySettings.company_details?.offertebijlage_auto_archive ? 22 : 2, top: 2,
                        width: 20, height: 20, borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                      }} />
                    </span>
                  </label>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>
                    Automatisch archiveren
                  </span>
                </div>
                {companySettings.company_details?.offertebijlage_auto_archive && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, paddingLeft: 56 }}>
                    <label style={{ fontSize: 13, whiteSpace: 'nowrap', color: '#92400e' }}>Na</label>
                    <input
                      type="number"
                      min={1}
                      value={companySettings.company_details?.offertebijlage_archive_days || 180}
                      onChange={(e) => handleCompanyDetailsChange('offertebijlage_archive_days', parseInt(e.target.value, 10) || 180)}
                      style={{ width: 70, padding: '5px 8px', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 13 }}
                    />
                    <span style={{ fontSize: 13, color: '#92400e' }}>dagen</span>
                  </div>
                )}
                <p style={{ margin: '8px 0 0 56px', fontSize: 12, color: '#9a3412' }}>
                  Offertebijlages worden dagelijks automatisch gearchiveerd in Odoo en verwijderd uit de database. Sla de instellingen op om te activeren.
                </p>
              </div>

              {/* Handmatig opschonen */}
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
                  Handmatig opschonen — Voorzichtig: dit archiveert Offertebijlage-producten ouder dan het opgegeven aantal dagen in Odoo en verwijdert ze uit de database. Dit kan niet ongedaan worden gemaakt.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Ouder dan</label>
                <input
                  type="number"
                  min={1}
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(parseInt(e.target.value, 10) || 90)}
                  style={{ width: 70, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                />
                <span style={{ fontSize: 13 }}>dagen</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                  disabled={cleanupLoading}
                  onClick={async () => {
                    if (!window.confirm(`Weet je zeker dat je alle Offertebijlage-producten ouder dan ${cleanupDays} dagen wilt opschonen? Dit kan niet ongedaan worden.`)) return;
                    setCleanupLoading(true);
                    setCleanupResult(null);
                    try {
                      const res = await api.post(`/api/subscribers/${selectedCompanyId}/cleanup-offertebijlage`, { days: cleanupDays });
                      setCleanupResult({ success: true, message: res.data?.message || 'Opschonen voltooid.' });
                    } catch (err) {
                      setCleanupResult({ success: false, message: err.response?.data?.error || 'Opschonen mislukt.' });
                    } finally {
                      setCleanupLoading(false);
                    }
                  }}
                >
                  {cleanupLoading ? 'Bezig...' : 'Opschonen'}
                </button>
              </div>
              {cleanupResult && (
                <div style={{ marginTop: 10, fontSize: 13, color: cleanupResult.success ? '#065f46' : '#991b1b' }}>
                  {cleanupResult.message}
                </div>
              )}
            </div>
          )}

          <div className="settings-actions">
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              {isPage ? 'Terug' : 'Annuleren'}
            </button>
            {selectedCompanyId && (
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Opslaan...' : 'Opslaan'}
              </button>
            )}
          </div>
        </form>
    </>
  );

  if (isPage) {
    return (
      <div className="settings-page">
        {content}
      </div>
    );
  }

  return (
    <div className="settings-overlay">
      <div className="settings-modal">
        {content}
      </div>
    </div>
  );
};

export default Settings;
